// src/pages/Maestros.tsx — SP-POST-09a: who is ready for the cascade (ops_maestro_readiness).
// A Maestro is ready when they have a US mobile, texting consent, the Compact, at least one active zone and
// Stripe payouts. Test accounts (contractors.is_test) are counted, not listed.
// AD-25b T6: License column — the number, whether it's verified (by whom, when), and a Verify / Undo button
//   (ops_set_license_verified; pricing_admin+; a reason is required and logged). While the license gate is on,
//   real Maestros need a verified license to be ready.
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { ops, type ContractorTitulo, type MaestroReadinessData } from "../lib/ops";
import { escudoSvg, OFICIO } from "@treesupply/mdo-ui";   // profile v1
import { dateTime } from "../lib/format";
import { Loading, Notice, PageFrame } from "../components/ui";

const CHECKS: { key: "phone_ok" | "sms_consent" | "compact" | "zones" | "stripe"; label: string }[] = [
  { key: "phone_ok", label: "Mobile" },
  { key: "sms_consent", label: "Texting consent" },
  { key: "compact", label: "Compact" },
  { key: "zones", label: "Zones" },
  { key: "stripe", label: "Stripe payouts" },
];

function Tick({ ok }: { ok: boolean }) {
  return <span aria-label={ok ? "done" : "missing"} style={{ fontWeight: 700, color: ok ? "#2E7D32" : "#C15834" }}>{ok ? "✓" : "✗"}</span>;
}

export default function Maestros() {
  const [data, setData] = useState<MaestroReadinessData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTest, setShowTest] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);   // T6
  const [titulos, setTitulos] = useState<Record<string, ContractorTitulo[]>>({});   // profile v1

  const load = useCallback(async () => {
    try {
      const d = await ops.maestroReadiness(showTest);
      setData(d);
      setError(null);
      // títulos are optional: if the call fails, the page still works without the column filled
      ops.contractorTitulos(d.maestros.map((m) => m.id)).then(setTitulos).catch(() => setTitulos({}));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [showTest]);
  useEffect(() => {
    void load();
  }, [load]);

  // T6: verify / un-verify a license (reason required, logged)
  const setVerified = async (id: string, name: string, verified: boolean) => {
    const reason = window.prompt(verified
      ? `Verify ${name}'s license. How did you check it? (e.g. "WA L&I lookup, active")`
      : `Remove verification for ${name}. Why?`);
    if (reason === null) return;
    setBusy(id);
    try {
      await ops.setLicenseVerified(id, verified, reason.trim());
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const cell: CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #E3DFD7", textAlign: "left", verticalAlign: "top" };

  return (
    <PageFrame title="Maestros" lede="Who the cascade can reach and pay. Every box needs a tick before a Maestro gets jobs by text.">
      {error && <Notice kind="error">{error}</Notice>}
      {!data && !error && <Loading what="Maestros" />}
      {data && (
        <>
          <p className="hint">
            {data.ready} of {data.maestros.length} ready · texts are {data.texts_live ? "ON" : "OFF"} · license gate {data.license_gate ? "ON" : "OFF"} · {data.test_accounts_hidden} test
            account{data.test_accounts_hidden === 1 ? "" : "s"} hidden
          </p>
          <label className="hint" style={{ display: "inline-flex", gap: 6, alignItems: "center", marginBottom: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} />
            Show test accounts{data.test_accounts_total ? ` (${data.test_accounts_total})` : ""} — they never get real customers' jobs
          </label>
          {data.maestros.length === 0 ? (
            <p className="empty-state">No real Maestros yet. They appear here once they pick a zone or accept the Compact.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={cell}>Maestro</th>
                    <th style={cell} title="Manos de Oro títulos GM may use for dispatch and pay. Job counts: Revisiones.">Títulos</th>
                    {CHECKS.map((c) => (
                      <th key={c.key} style={{ ...cell, textAlign: "center" }}>{c.label}</th>
                    ))}
                    <th style={cell}>License</th>
                    <th style={cell}>Still needs</th>
                  </tr>
                </thead>
                <tbody>
                  {data.maestros.map((m) => (
                    <tr key={m.id}>
                      <td style={cell}>
                        <strong>{m.name}</strong>
                        {m.is_test && (
                          <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#EDE7DA", color: "#8C6D3F" }}>TEST</span>
                        )}
                        {m.business_name ? <> · {m.business_name}</> : null}
                        <div className="hint">
                          {m.titulo ?? "Sin título"} · joined {dateTime(m.joined)}
                          {m.phone ? <> · <a href={`tel:${m.phone}`}>{m.phone}</a></> : null}
                          {m.email ? <> · <a href={`mailto:${m.email}`}>{m.email}</a></> : null}
                        </div>
                      </td>
                      <td style={cell}>
                        {(titulos[m.id] ?? []).length === 0 ? <span className="hint">—</span> : (titulos[m.id] ?? []).map((t) => (
                          <div key={t.oficio + t.holder} style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                            <span aria-hidden dangerouslySetInnerHTML={{ __html: t.oficio in OFICIO ? escudoSvg(t.oficio as keyof typeof OFICIO, 18) : "" }} />
                            <span>{t.titulo[0].toUpperCase() + t.titulo.slice(1)} · {(OFICIO as Record<string, { es: string }>)[t.oficio]?.es ?? t.oficio}</span>
                            {t.holder === "crew" && <span className="hint">(crew)</span>}
                            {t.visibility !== "publico" && <span className="hint" title="Private: GM may use it; owners never see it">🔒</span>}
                          </div>
                        ))}
                      </td>
                      <td style={{ ...cell, textAlign: "center" }}><Tick ok={m.phone_ok} /></td>
                      <td style={{ ...cell, textAlign: "center" }}><Tick ok={m.sms_consent} /></td>
                      <td style={{ ...cell, textAlign: "center" }}><Tick ok={m.compact} /></td>
                      <td style={{ ...cell, textAlign: "center" }} title={m.zips.join(", ")}>
                        <Tick ok={m.active_zones > 0} /> <span className="hint">{m.active_zones}</span>
                      </td>
                      <td style={{ ...cell, textAlign: "center" }}><Tick ok={m.stripe} /></td>
                      <td style={cell}>
                        {m.license_number ? <code>{m.license_number}</code> : <span className="hint">none yet</span>}
                        {m.license_verified ? (
                          <div className="hint">
                            <Tick ok /> verified{m.license_verified_by ? ` by ${m.license_verified_by}` : ""}
                            {m.license_verified_at ? ` · ${dateTime(m.license_verified_at)}` : ""}{" "}
                            <button type="button" disabled={busy === m.id} onClick={() => void setVerified(m.id, m.name, false)}
                              style={{ fontSize: 12, marginLeft: 4 }}>Undo</button>
                          </div>
                        ) : m.license_number ? (
                          <div>
                            <button type="button" disabled={busy === m.id} onClick={() => void setVerified(m.id, m.name, true)}
                              style={{ fontSize: 12, marginTop: 4 }}>{busy === m.id ? "Saving…" : "Verify license"}</button>
                          </div>
                        ) : null}
                      </td>
                      <td style={cell}>{m.ready ? <strong style={{ color: "#2E7D32" }}>Ready</strong> : m.missing.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </PageFrame>
  );
}
